/**
 * DV-PROJ-001: Projects React Query Hooks
 *
 * React Query hooks for the Projects tab functionality following
 * existing dashboard patterns with optimistic updates and caching.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProjectsAPIService } from '@/services/projects-api';
import type {
  // Project types
  CreateProjectRequest,
  UpdateProjectRequest,

  // Character Profile types
  CreateCharacterProfileRequest,
  UpdateCharacterProfileRequest,

  // Setting Profile types
  CreateSettingProfileRequest,
  UpdateSettingProfileRequest,

  // Location Profile types
  CreateLocationProfileRequest,
  UpdateLocationProfileRequest,

  // Asset types
  CreateAssetRequest,
  UpdateAssetRequest,

  // Creative Profile types
  UpdateProjectCreativeProfileRequest,

  // Seed generation types
  CreateSeedGenerationJobRequest,

  // Upload types
  FileUploadRequest,
} from '@/types/projects';

// Query Keys
export const projectsQueryKeys = {
  // Projects
  projects: ['projects'] as const,
  projectsList: (params?: { search?: string }) => ['projects', 'list', params] as const,
  project: (projectId: string) => ['projects', projectId] as const,

  // Character Profiles
  characters: (projectId: string) => ['projects', projectId, 'characters'] as const,
  character: (projectId: string, characterId: string) => ['projects', projectId, 'characters', characterId] as const,

  // Setting Profiles (Legacy)
  settings: (projectId: string) => ['projects', projectId, 'settings'] as const,
  setting: (projectId: string, settingId: string) => ['projects', projectId, 'settings', settingId] as const,

  // Location Profiles (New)
  locations: (projectId: string) => ['projects', projectId, 'locations'] as const,
  location: (projectId: string, locationId: string) => ['projects', projectId, 'locations', locationId] as const,

  // Assets
  assets: (projectId: string, type?: string) => ['projects', projectId, 'assets', type] as const,
  asset: (projectId: string, assetId: string) => ['projects', projectId, 'assets', assetId] as const,

  // Creative Profiles
  creativeProfiles: ['creative-profiles'] as const,
  projectCreativeProfiles: (projectId: string) => ['projects', projectId, 'creative-profiles'] as const,

  // Seed Generation
  seedJobs: (projectId: string, characterId?: string) =>
    ['projects', projectId, 'seed-jobs', characterId] as const,
  seedJob: (jobId: string) => ['seed-jobs', jobId] as const,
} as const;

// === PROJECT QUERIES ===

export function useProjectsQuery(params?: { search?: string }) {
  return useQuery({
    queryKey: projectsQueryKeys.projectsList(params),
    queryFn: () => ProjectsAPIService.getProjects(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useProjectQuery(projectId: string | null) {
  return useQuery({
    queryKey: projectsQueryKeys.project(projectId!),
    queryFn: () => ProjectsAPIService.getProject(projectId!),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// === PROJECT MUTATIONS ===

export function useCreateProjectMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProjectRequest) => ProjectsAPIService.createProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.projects });
    },
  });
}

export function useUpdateProjectMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: UpdateProjectRequest }) =>
      ProjectsAPIService.updateProject(projectId, data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.projects });
      queryClient.setQueryData(projectsQueryKeys.project(variables.projectId), result);
    },
  });
}

export function useDeleteProjectMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId: string) => ProjectsAPIService.deleteProject(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.projects });
    },
  });
}

export function useDuplicateProjectMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, name }: { projectId: string; name: string }) =>
      ProjectsAPIService.duplicateProject(projectId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.projects });
    },
  });
}

// === CHARACTER PROFILE QUERIES ===

export function useCharacterProfilesQuery(projectId: string | null) {
  return useQuery({
    queryKey: projectsQueryKeys.characters(projectId!),
    queryFn: () => ProjectsAPIService.getCharacterProfiles(projectId!),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    select: (raw: any) => {
      // Normalize API response shape - handle different possible structures
      const characters =
        (Array.isArray(raw) ? raw :
         raw?.characters ??
         raw?.items ??
         raw?.data?.characters ??
         []);
      const total =
        raw?.total ?? raw?.count ?? raw?.data?.total ?? characters.length;
      return { characters, total };
    },
  });
}

export function useCharacterProfileQuery(projectId: string | null, characterId: string | null) {
  return useQuery({
    queryKey: projectsQueryKeys.character(projectId!, characterId!),
    queryFn: () => ProjectsAPIService.getCharacterProfile(projectId!, characterId!),
    enabled: !!projectId && !!characterId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// === CHARACTER PROFILE MUTATIONS ===

export function useCreateCharacterProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: CreateCharacterProfileRequest }) =>
      ProjectsAPIService.createCharacterProfile(projectId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.characters(variables.projectId) });
    },
  });
}

export function useUpdateCharacterProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      characterId,
      data
    }: {
      projectId: string;
      characterId: string;
      data: UpdateCharacterProfileRequest
    }) => ProjectsAPIService.updateCharacterProfile(projectId, characterId, data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.characters(variables.projectId) });
      queryClient.setQueryData(
        projectsQueryKeys.character(variables.projectId, variables.characterId),
        result
      );
    },
  });
}

export function useDeleteCharacterProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, characterId }: { projectId: string; characterId: string }) =>
      ProjectsAPIService.deleteCharacterProfile(projectId, characterId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.characters(variables.projectId) });
    },
  });
}

// === SETTING PROFILE QUERIES ===

export function useSettingProfilesQuery(projectId: string | null) {
  return useQuery({
    queryKey: projectsQueryKeys.settings(projectId!),
    queryFn: () => ProjectsAPIService.getSettingProfiles(projectId!),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    select: (raw: any) => {
      // Normalize API response shape - handle different possible structures
      const settings =
        (Array.isArray(raw) ? raw :
         raw?.settings ??
         raw?.items ??
         raw?.data?.settings ??
         []);
      const total =
        raw?.total ?? raw?.count ?? raw?.data?.total ?? settings.length;
      return { settings, total };
    },
  });
}

export function useSettingProfileQuery(projectId: string | null, settingId: string | null) {
  return useQuery({
    queryKey: projectsQueryKeys.setting(projectId!, settingId!),
    queryFn: () => ProjectsAPIService.getSettingProfile(projectId!, settingId!),
    enabled: !!projectId && !!settingId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// === SETTING PROFILE MUTATIONS ===

export function useCreateSettingProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: CreateSettingProfileRequest }) =>
      ProjectsAPIService.createSettingProfile(projectId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.settings(variables.projectId) });
    },
  });
}

export function useUpdateSettingProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      settingId,
      data
    }: {
      projectId: string;
      settingId: string;
      data: UpdateSettingProfileRequest
    }) => ProjectsAPIService.updateSettingProfile(projectId, settingId, data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.settings(variables.projectId) });
      queryClient.setQueryData(
        projectsQueryKeys.setting(variables.projectId, variables.settingId),
        result
      );
    },
  });
}

export function useDeleteSettingProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, settingId }: { projectId: string; settingId: string }) =>
      ProjectsAPIService.deleteSettingProfile(projectId, settingId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.settings(variables.projectId) });
    },
  });
}

// === LOCATION PROFILE QUERIES ===

export function useLocationProfilesQuery(projectId: string | null) {
  return useQuery({
    queryKey: projectsQueryKeys.locations(projectId!),
    queryFn: () => ProjectsAPIService.getLocationProfiles(projectId!),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    select: (raw: any) => {
      // Normalize API response shape - handle different possible structures
      const locations =
        (Array.isArray(raw) ? raw :
         raw?.locations ??
         raw?.items ??
         raw?.data?.locations ??
         []);
      const total =
        raw?.total ?? raw?.count ?? raw?.data?.total ?? locations.length;
      return { locations, total };
    },
  });
}

export function useLocationProfileQuery(projectId: string | null, locationId: string | null) {
  return useQuery({
    queryKey: projectsQueryKeys.location(projectId!, locationId!),
    queryFn: () => ProjectsAPIService.getLocationProfile(projectId!, locationId!),
    enabled: !!projectId && !!locationId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// === LOCATION PROFILE MUTATIONS ===

export function useCreateLocationProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: CreateLocationProfileRequest }) =>
      ProjectsAPIService.createLocationProfile(projectId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.locations(variables.projectId) });
    },
  });
}

export function useUpdateLocationProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      locationId,
      data
    }: {
      projectId: string;
      locationId: string;
      data: UpdateLocationProfileRequest
    }) => ProjectsAPIService.updateLocationProfile(projectId, locationId, data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.locations(variables.projectId) });
      queryClient.setQueryData(
        projectsQueryKeys.location(variables.projectId, variables.locationId),
        result
      );
    },
  });
}

export function useDeleteLocationProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, locationId }: { projectId: string; locationId: string }) =>
      ProjectsAPIService.deleteLocationProfile(projectId, locationId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.locations(variables.projectId) });
    },
  });
}

// === ASSET QUERIES ===

export function useAssetsQuery(projectId: string | null, type?: string) {
  return useQuery({
    queryKey: projectsQueryKeys.assets(projectId!, type),
    queryFn: () => ProjectsAPIService.getAssets(projectId!, { type }),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useAssetQuery(projectId: string | null, assetId: string | null) {
  return useQuery({
    queryKey: projectsQueryKeys.asset(projectId!, assetId!),
    queryFn: () => ProjectsAPIService.getAsset(projectId!, assetId!),
    enabled: !!projectId && !!assetId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// === ASSET MUTATIONS ===

export function useCreateAssetMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: CreateAssetRequest }) =>
      ProjectsAPIService.createAsset(projectId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.assets(variables.projectId) });
    },
  });
}

export function useUpdateAssetMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      assetId,
      data
    }: {
      projectId: string;
      assetId: string;
      data: UpdateAssetRequest
    }) => ProjectsAPIService.updateAsset(projectId, assetId, data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.assets(variables.projectId) });
      queryClient.setQueryData(
        projectsQueryKeys.asset(variables.projectId, variables.assetId),
        result
      );
    },
  });
}

export function useDeleteAssetMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, assetId }: { projectId: string; assetId: string }) =>
      ProjectsAPIService.deleteAsset(projectId, assetId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: projectsQueryKeys.assets(variables.projectId) });
    },
  });
}

// === CREATIVE PROFILES QUERIES ===

export function useCreativeProfilesQuery() {
  return useQuery({
    queryKey: projectsQueryKeys.creativeProfiles,
    queryFn: () => ProjectsAPIService.getCreativeProfiles(),
    staleTime: 1000 * 60 * 10, // 10 minutes (global data, changes infrequently)
  });
}

export function useProjectCreativeProfilesQuery(projectId: string | null) {
  return useQuery({
    queryKey: projectsQueryKeys.projectCreativeProfiles(projectId!),
    queryFn: () => ProjectsAPIService.getProjectCreativeProfiles(projectId!),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// === CREATIVE PROFILES MUTATIONS ===

export function useUpdateProjectCreativeProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      creativeProfileId,
      data
    }: {
      projectId: string;
      creativeProfileId: string;
      data: UpdateProjectCreativeProfileRequest
    }) => ProjectsAPIService.updateProjectCreativeProfile(projectId, creativeProfileId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: projectsQueryKeys.projectCreativeProfiles(variables.projectId)
      });
    },
  });
}

// === SEED GENERATION QUERIES ===

export function useSeedGenerationJobsQuery(projectId: string | null, characterId?: string) {
  return useQuery({
    queryKey: projectsQueryKeys.seedJobs(projectId!, characterId),
    queryFn: () => ProjectsAPIService.getSeedGenerationJobs(projectId!, characterId),
    enabled: !!projectId,
    staleTime: 1000 * 30, // 30 seconds (more frequent updates for job status)
  });
}

export function useSeedGenerationJobQuery(jobId: string | null) {
  return useQuery({
    queryKey: projectsQueryKeys.seedJob(jobId!),
    queryFn: () => ProjectsAPIService.getSeedGenerationJob(jobId!),
    enabled: !!jobId,
    staleTime: 1000 * 10, // 10 seconds (frequent updates for job status)
    refetchInterval: (query) => {
      // Auto-refetch running jobs every 5 seconds
      if (query.state.data?.job?.status === 'running' || query.state.data?.job?.status === 'queued') {
        return 5000;
      }
      return false;
    },
  });
}

// === SEED GENERATION MUTATIONS ===

export function useCreateSeedGenerationJobMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      characterId,
      data
    }: {
      projectId: string;
      characterId: string;
      data: CreateSeedGenerationJobRequest
    }) => ProjectsAPIService.createSeedGenerationJob(projectId, characterId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: projectsQueryKeys.seedJobs(variables.projectId, variables.characterId)
      });
    },
  });
}

// === FILE UPLOAD MUTATIONS ===

export function useFileUploadMutation() {
  return useMutation({
    mutationFn: (data: FileUploadRequest) => ProjectsAPIService.uploadFile(data),
  });
}

export function useSignedUploadUrlMutation() {
  return useMutation({
    mutationFn: ({
      fileName,
      fileType,
      projectId
    }: {
      fileName: string;
      fileType: string;
      projectId?: string
    }) => ProjectsAPIService.getSignedUploadUrl(fileName, fileType, projectId),
  });
}